import { CircularProgress } from '@mui/material'
import type { PurchaseOrderWithUsage } from '@/types/po'
import { useT } from '@/i18n/useT'
import {
  EmptyState,
  GridCell,
  GridHead,
  GridRow,
  GridTableShell,
  Icon,
  IconButton,
  MiniBar,
  Pill,
} from '@/components/ui'

interface POTableProps {
  pos: PurchaseOrderWithUsage[]
  loading: boolean
  onEdit: (po: PurchaseOrderWithUsage) => void
  onDelete: (id: string) => void
  onFilter: (poId: string | null) => void
  filteredPOId: string | null
}

const TEMPLATE =
  '130px 110px minmax(180px, 1.3fr) minmax(200px, 1.4fr) 160px 130px 150px 130px 130px 96px'
const MIN_WIDTH = 1580

function formatEUR(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)
}

/** Consumption colour: green while there is headroom, amber near the cap, red over. */
function usageColor(fraction: number): string {
  if (fraction > 1) return 'var(--err)'
  if (fraction >= 0.85) return 'var(--warn)'
  return 'var(--accent)'
}

export function POTable({ pos, loading, onEdit, onDelete, onFilter, filteredPOId }: POTableProps) {
  const { t } = useT()

  if (loading) {
    return (
      <GridTableShell>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress />
        </div>
      </GridTableShell>
    )
  }

  if (pos.length === 0) {
    return (
      <EmptyState
        title={t('No POs for this year')}
        hint={t('Create a new PO or import the PO list.')}
      />
    )
  }

  return (
    <GridTableShell>
      <div className="fx-scroll-x">
        <GridHead
          template={TEMPLATE}
          minWidth={MIN_WIDTH}
          columns={[
            { key: 'number', label: t('PO number') },
            { key: 'pivot', label: t('Pivot PO') },
            { key: 'supplier', label: t('Supplier') },
            { key: 'desc', label: t('Description') },
            { key: 'responsible', label: t('Responsible person') },
            { key: 'budget', label: t('Total budget'), align: 'right' },
            { key: 'usage', label: t('Usage') },
            { key: 'used', label: t('Used'), align: 'right' },
            { key: 'remaining', label: t('Remaining'), align: 'right' },
            { key: 'actions', label: '', align: 'center' },
          ]}
        />
        {pos.map((po, i) => {
          const fraction = po.valueTotalEur > 0 ? po.valueUsed / po.valueTotalEur : 0
          const selected = filteredPOId === po.id
          return (
            <GridRow
              key={po.id}
              template={TEMPLATE}
              minWidth={MIN_WIDTH}
              zebra={i % 2 === 1}
              style={
                selected
                  ? { background: 'var(--accentSoft)', boxShadow: 'inset 3px 0 0 var(--accent)' }
                  : undefined
              }
            >
              <GridCell mono>{po.poNumber}</GridCell>
              <GridCell color="var(--muted)" size={12}>
                {po.pivotPoNumber || '—'}
              </GridCell>
              <GridCell strong>{po.supplierName || '—'}</GridCell>
              <GridCell color="var(--muted)" size={12}>
                {po.description || '—'}
              </GridCell>
              <GridCell size={12} color={po.responsiblePerson ? 'var(--text)' : 'var(--faint)'}>
                {po.responsiblePerson || '—'}
              </GridCell>
              <GridCell align="right">{formatEUR(po.valueTotalEur)}</GridCell>
              <div style={{ minWidth: 0 }}>
                <MiniBar fraction={fraction} color={usageColor(fraction)} />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                  {Math.round(fraction * 100)} %
                </div>
              </div>
              <GridCell align="right" strong color={po.isOverrun ? 'var(--err)' : 'var(--text)'}>
                {formatEUR(po.valueUsed)}
              </GridCell>
              <GridCell
                align="right"
                strong
                color={po.valueRemaining < 0 ? 'var(--err)' : 'var(--text)'}
              >
                {formatEUR(Math.max(0, po.valueRemaining))}
              </GridCell>
              <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <IconButton
                  title={selected ? t('Clear filter') : t('Show only this PO in the chart')}
                  onClick={() => onFilter(selected ? null : po.id)}
                >
                  <Icon name="eye" size={16} style={selected ? { color: 'var(--accent)' } : undefined} />
                </IconButton>
                <IconButton title={t('Edit')} onClick={() => onEdit(po)}>
                  <Icon name="settings" size={15} strokeWidth={1.8} />
                </IconButton>
                <IconButton title={t('Delete')} variant="danger" onClick={() => onDelete(po.id)}>
                  <Icon name="trash" size={16} />
                </IconButton>
              </div>
            </GridRow>
          )
        })}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--muted)', flex: 1 }}>
          {pos.length} PO{pos.length === 1 ? '' : 's'}
        </div>
        {pos.some((po) => po.isOverrun) && (
          <Pill fg="var(--err)" bg="var(--errSoft)" border="var(--err)">
            {pos.filter((po) => po.isOverrun).length} {t('over budget')}
          </Pill>
        )}
      </div>
    </GridTableShell>
  )
}
